import axios, {
	type AxiosError,
	type AxiosInstance,
	type AxiosProgressEvent,
	type AxiosRequestConfig,
	type AxiosResponse,
} from 'axios';
import * as Sentry from '@sentry/react-native';
import Config from 'src/core/utils/Config';

import { authEventBus, apiEventBus } from 'src/core/events/Events';
import type { ApiError } from 'src/core/models/api/ApiError';
import type { ApiEventResponse } from 'src/core/models/api/responses/ApiEventResponse';
import type { ApiFormSubmitResponse } from 'src/core/models/api/responses/ApiFormSubmitResponse';
import type { ApiLoginResponse } from 'src/core/models/api/responses/ApiLoginResponse';
import type { ApiRegisterResponse } from 'src/core/models/api/responses/ApiRegisterResponse';
import type { ApiStatusResponse } from 'src/core/models/api/responses/ApiStatusResponse';
import type { ApiFavoritesResponse } from 'src/core/models/api/responses/ApiFavoritesResponse';
import type { ApiFavoriteToggleResponse } from 'src/core/models/api/responses/ApiFavoriteToggleResponse';
import type { ApiValidateEventsResponse } from 'src/core/models/api/responses/ApiValidateEventsResponse';
import type ApiSettings from 'src/core/models/api/ApiSettings';
import type { ApiFormSubmission } from 'src/core/models/api/ApiFormSubmission';
import type { ApiEventSubmission } from 'src/core/models/api/ApiEventSubmission';
import type { ApiOnBehalfRegistration } from 'src/core/models/api/ApiOnBehalfRegistration';
import type { ApiGallery } from 'src/core/models/api/ApiGallery';
import type { ApiNotification } from 'src/core/models/api/ApiNotification';
import type { ApiContentBlockForm } from 'src/core/models/api/ApiContentBlock';
import type { ApiPage } from 'src/core/models/api/ApiPage';
import type { ApiUser } from 'src/core/models/api/ApiUser';
import Constants from 'src/core/utils/Constants';
import LoggingService from 'src/data/services/LoggingService';
import SecureStorageService from 'src/data/services/SecureStorageService';

declare module 'axios' {
	interface InternalAxiosRequestConfig {
		metadata?: { startTime: number };
	}
	interface AxiosRequestConfig {
		// Set on explicit user-action calls (redeem, form submit, feedback, favorites) that show their own failure
		// feedback, so the global error toast does not double up.
		skipErrorToast?: boolean;
	}
}

const TAG = 'ApiService';
const DEFAULT_TIMEOUT = 30000;

/**
 * Whether the failed request actually carried the auth token.
 *
 * A 401 only means "your session is gone" when the server saw a token and rejected it. Without one it means
 * the app never sent it -- SecureStore cannot always be read (an iOS keychain on a locked device is the known
 * case) and `getAuthToken()` returns null for both "no token" and "unreadable". Logging out on that 401 threw
 * users out of a session the server still considers valid.
 */
function requestCarriedToken(headers: unknown): boolean {
	if (!headers) {
		return false;
	}

	const asAxiosHeaders = headers as { get?: (name: string) => unknown };
	if (typeof asAxiosHeaders.get === 'function') {
		return Boolean(asAxiosHeaders.get(Constants.apiHeaderToken));
	}

	return Boolean((headers as Record<string, unknown>)[Constants.apiHeaderToken]);
}

class ApiService {
	private apiClient: AxiosInstance;
	public readonly endpoints: ReturnType<ApiService['_buildEndpoints']>;

	public constructor() {
		LoggingService.info(TAG, `init() -> using API base URL: ${Config.apiUrl}`);
		this.apiClient = this._initApiClient();
		this.endpoints = this._buildEndpoints();
	}

	public getEndpoints() {
		return this.endpoints;
	}

	private _initApiClient(): AxiosInstance {
		const apiClient = axios.create({
			baseURL: Config.apiUrl,
			headers: {
				[Constants.apiHeaderContentType]: 'application/json',
				[Constants.apiHeaderAccept]: 'application/json, text/plain, */*',
			},
			timeout: DEFAULT_TIMEOUT,
		});

		apiClient.interceptors.request.use(async (request) => {
			const authToken = await SecureStorageService.getAuthToken();
			if (authToken && !request.headers.get(Constants.apiHeaderToken)) {
				request.headers.set(Constants.apiHeaderToken, authToken);
			}

			const url = request.url ?? '';
			const method = (request.method ?? 'GET').toUpperCase();
			LoggingService.info(TAG, `-> ${method} ${url}`);
			request.metadata = { startTime: Date.now() };

			return request;
		});

		apiClient.interceptors.response.use(
			(res) => {
				const config = res.config;
				const url = config.url ?? '';
				const method = (config.method ?? 'GET').toUpperCase();
				const status = res.status;
				const duration = config.metadata?.startTime ? Date.now() - config.metadata.startTime : 0;

				LoggingService.info(TAG, `<- ${status} ${method} ${url} (${duration}ms)`);

				Sentry.addBreadcrumb({
					category: 'api',
					level: 'info',
					data: { method, url, status, duration_ms: duration },
				});

				// Signal recovery so the global error toast re-arms: one toast per outage, not per tab.
				apiEventBus.emit(Constants.eventApiOnSuccess);

				return res;
			},
			(error: AxiosError<ApiError>) => {
				const config = error.response?.config ?? error.config;
				const status = error.response?.status;
				const url = config?.url ?? '';
				const method = (config?.method ?? 'GET').toUpperCase();
				const duration = config?.metadata?.startTime ? Date.now() - config.metadata.startTime : 0;

				LoggingService.error(TAG, `<- ${status ?? 'NO_STATUS'} ${method} ${url} (${duration}ms)`, error.response?.data);

				Sentry.addBreadcrumb({
					category: 'api',
					level: 'error',
					data: { method, url, status, duration_ms: duration },
				});

				if (status === 401) {
					// A 401 is never toast material, so it stays out of the branch below either way.
					if (requestCarriedToken(config?.headers)) {
						authEventBus.emit(Constants.eventAuthOnUnauthorized);
					}
				} else if (!axios.isCancel(error) && !config?.skipErrorToast) {
					// Surface every other failure (network/timeout or a non-2xx response) so the global toast can tell the
					// user the call failed instead of silently showing stale data.
					apiEventBus.emit(Constants.eventApiOnError, { kind: status ? 'server' : 'network', status });
				}

				return Promise.reject(error);
			},
		);

		return apiClient;
	}

	private _buildEndpoints() {
		const unwrap = <T>(response: AxiosResponse<T>) => response;

		const request = {
			get: <T>(url: string, config?: AxiosRequestConfig) => this.apiClient.get<T>(url, config).then(unwrap),
			post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
				this.apiClient.post<T>(url, body, config).then(unwrap),
			put: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
				this.apiClient.put<T>(url, body, config).then(unwrap),
			delete: <T>(url: string, config?: AxiosRequestConfig) => this.apiClient.delete<T>(url, config).then(unwrap),
		};

		const appVersion = Config.appVersion;

		const auth = {
			// Auth calls show their own inline/alert feedback, so they opt out of the global error toast.
			login: ({ email, password, pushToken }: { email: string; password: string; pushToken?: string | null }) =>
				request.post<ApiLoginResponse>(
					'user/login',
					{
						email,
						wachtwoord: password,
						version: appVersion,
						push_token: pushToken,
					},
					{ skipErrorToast: true },
				),

			register: ({
				email,
				password,
				firstname,
				lastname,
				eventId,
				source,
			}: {
				email: string;
				password: string;
				firstname: string;
				lastname: string;
				eventId?: string;
				source?: string;
			}) =>
				request.post<ApiRegisterResponse>(
					'user/register',
					{
						email,
						wachtwoord: password,
						voornaam: firstname,
						achternaam: lastname,
						event_id: eventId,
						source,
					},
					{ skipErrorToast: true },
				),

			forgotPassword: ({ email }: { email: string }) =>
				request.post<ApiStatusResponse>('user/password/forgot', { email }, { skipErrorToast: true }),

			resetPassword: ({ token, password }: { token: string; password: string }) =>
				request.post<ApiStatusResponse>(`user/password/reset/${token}`, { password, token }),
		};

		const user = {
			getProfile: ({ pushToken }: { pushToken?: string | null } = {}) =>
				request.get<ApiUser>('user', { params: { push_token: pushToken, version: appVersion } }),

			updateProfile: (profile: Record<string, unknown>) => request.post<ApiStatusResponse>('user', profile),

			deleteAccount: () => request.delete<ApiStatusResponse>('user/delete'),

			addEvents: ({ events }: { events: string[] }) => request.post<void>('user/events', { events }),

			deleteEvents: ({ events }: { events: string[] }) => request.post<void>('user/events/delete', { events }),
		};

		const events = {
			getEvent: ({ eventUuid, noCache }: { eventUuid: string; noCache?: boolean }) =>
				request.get<ApiEventResponse>(`event/${eventUuid}`, noCache ? noCacheConfig() : undefined),

			redeem: ({ code }: { code: string }) => request.get<ApiEventResponse>(`redeem/${code}`, { skipErrorToast: true }),

			// Read-only existence check (returns how many events carry this code).
			checkCode: ({ code }: { code: string }) =>
				request.post<number>('checkeventcode', { code }, { skipErrorToast: true }),

			validateIds: ({ ids }: { ids: string[] }) => request.post<ApiValidateEventsResponse>('events/validate', { ids }),

			// Both ask for the state of one event right now (a page with its blocks, a form with its options and
			// the room left in them), so neither may come out of a cache along the way.
			getPage: ({ eventUuid, pageId }: { eventUuid: string; pageId: string | number }) =>
				request.get<ApiPage>(`event/${eventUuid}/page/${pageId}`, noCacheConfig()),

			getForm: ({ eventUuid, formId }: { eventUuid: string; formId: string | number }) =>
				request.get<ApiContentBlockForm>(`event/${eventUuid}/forms/${formId}`, {
					...noCacheConfig(),
					skipErrorToast: true,
				}),
		};

		const mobileDevices = {
			// Single canonical device registration (per install).
			register: ({
				pushToken,
				installationId,
				deviceName,
				deviceModel,
				platformName,
				platformVersion,
				eventUuids,
			}: {
				pushToken: string;
				installationId: string;
				deviceName: string;
				deviceModel: string;
				platformName: string;
				platformVersion: string;
				eventUuids: string[];
			}) =>
				request.post<ApiStatusResponse>(
					'mobile-devices/register',
					{
						push_token: pushToken,
						device_id: installationId,
						device_name: deviceName,
						device_model: deviceModel,
						platform_name: platformName,
						platform_version: platformVersion,
						app_version: appVersion,
						event_uuids: eventUuids,
					},
					noStoreConfig(),
				),
		};

		const galleries = {
			eventGalleries: ({ eventUuids }: { eventUuids: string }) =>
				request.get<ApiGallery[]>('galleries', { params: { eventIDs: eventUuids } }),

			upload: ({
				eventUuid,
				galleryId,
				payload,
				onUploadProgress,
			}: {
				eventUuid: string;
				galleryId: string | number;
				payload: Record<string, unknown>;
				onUploadProgress?: (event: AxiosProgressEvent) => void;
			}) =>
				// skipErrorToast: GalleryHolderScreen shows its own dialog when an upload fails, and the
				// generic toast on top of it said the same thing twice in two different words.
				request.post<ApiStatusResponse>(`event/${eventUuid}/${galleryId}/galleries/upload`, payload, {
					onUploadProgress,
					skipErrorToast: true,
				}),
		};

		const forms = {
			submit: ({
				formId,
				answers,
				choices,
				pushToken,
				userToken,
				previousSubmission,
				onBehalf,
			}: {
				formId: string | number;
				answers: Record<string, unknown>;
				choices?: Record<string, string[]>;
				pushToken?: string | null;
				userToken?: string | null;
				previousSubmission?: string | number | null;
				onBehalf?: boolean;
			}) =>
				request.post<ApiFormSubmitResponse>(
					`form/${formId}/submit`,
					{
						answers,
						choices,
						form_id: formId,
						push_token: pushToken,
						user_token: userToken,
						previous_submission: previousSubmission,
						on_behalf: onBehalf ? true : undefined,
					},
					{ skipErrorToast: true },
				),

			getSubmission: ({ formId, id }: { formId: string | number; id: string | number }) =>
				request.get<ApiFormSubmission>(`form/${formId}/submissions/${id}`, { skipErrorToast: true }),

			deleteSubmission: ({ formId, id }: { formId: string | number; id: string | number }) =>
				// skipErrorToast: cancelSubmission handles its own feedback, including the expected 422 when the
				// cancellation deadline has passed, so the global server-error toast must not also fire.
				request.post<ApiStatusResponse>(`form/${formId}/submissions/${id}/delete`, {}, { skipErrorToast: true }),

			sendFeedback: (payload: Record<string, unknown>) =>
				request.post<ApiStatusResponse>('form', payload, { skipErrorToast: true }),
		};

		const push = {
			enable: ({ pushToken }: { pushToken: string }) =>
				request.post<ApiStatusResponse>('push/enable', { push_token: pushToken }),

			disable: ({ pushToken }: { pushToken: string }) =>
				request.post<ApiStatusResponse>('push/disable', { push_token: pushToken }),
		};

		const notifications = {
			getByToken: ({ pushToken }: { pushToken: string }) => request.get<ApiNotification[]>(`notifications/${pushToken}`),
		};

		const analytics = {
			post: ({ eventUuid, type, data }: { eventUuid: string; type: string; data?: unknown }) =>
				request.post<ApiStatusResponse>(`event/${eventUuid}/analytics`, { type, data }),
		};

		const favorites = {
			list: ({ eventUuid }: { eventUuid: string }) =>
				request.get<ApiFavoritesResponse>(`event/${eventUuid}/favorites`, { skipErrorToast: true }),

			toggle: ({ eventUuid, pageId }: { eventUuid: string; pageId: string | number }) =>
				request.post<ApiFavoriteToggleResponse>(`event/${eventUuid}/page/${pageId}/favorite`, {}, { skipErrorToast: true }),

			// A timetable session is not a page, so it is toggled by its uid (#402).
			toggleSession: ({ eventUuid, uid }: { eventUuid: string; uid: string }) =>
				request.post<ApiFavoriteToggleResponse>(`event/${eventUuid}/session/${uid}/favorite`, {}, { skipErrorToast: true }),
		};

		const settings = {
			// Public, unauthenticated.
			get: () => request.get<ApiSettings>('settings', { skipErrorToast: true }),
		};

		const submissions = {
			// The logged-in user's personal registration (QR + registration number) for one event.
			getMine: ({ eventIdentifier, signal }: { eventIdentifier: string; signal?: AbortSignal }) =>
				request.get<ApiEventSubmission>(`user/events/${eventIdentifier}/submission`, {
					skipErrorToast: true,
					signal,
				}),

			// The registrations the logged-in user made for other people for one event.
			listOnBehalf: ({ eventIdentifier }: { eventIdentifier: string }) =>
				request.get<ApiOnBehalfRegistration[]>(`user/events/${eventIdentifier}/on-behalf`, { skipErrorToast: true }),
		};

		return {
			auth,
			user,
			events,
			mobileDevices,
			galleries,
			forms,
			push,
			notifications,
			analytics,
			favorites,
			settings,
			submissions,
		};
	}
}

function noCacheConfig(): AxiosRequestConfig {
	return {
		headers: {
			[Constants.apiHeaderCacheControl]: 'no-cache, no-store, must-revalidate',
			[Constants.apiHeaderPragma]: 'no-cache',
			[Constants.apiHeaderExpires]: '0',
		},
	};
}

function noStoreConfig(): AxiosRequestConfig {
	return {
		headers: {
			[Constants.apiHeaderCacheControl]: 'no-store',
		},
	};
}

export default new ApiService();
